import React, { useMemo, useState } from "react";
import { Alert as AntAlert } from "antd";
import { DownOutlined, UpOutlined } from "@ant-design/icons";

/**
 * جایگزینِ سراسریِ `Alert` — همان API، ولی **فشرده**.
 *
 * چرا: کادرهای توضیحی در همهٔ صفحات چند خط بودند و نیمِ صفحه را می‌گرفتند؛
 * کارِ اصلیِ کاربر هل می‌خورد پایین. اینجا عنوان همیشه یک خط است و هر چیزی که
 * بیشتر از یک خط باشد پشتِ «بیشتر» جمع می‌شود.
 *
 * چیزی از رفتارِ قبلی کم نمی‌شود: همهٔ propهای antd پاس داده می‌شوند و متنِ
 * توضیح حذف نمی‌شود — فقط تا کلیک نکنی باز نمی‌شود. توضیحِ کوتاه (یک‌خطی)
 * همان‌طور که بود نشان داده می‌شود، چون جمع‌کردنش فقط یک کلیکِ اضافه است.
 */

/** آیا این توضیح آن‌قدر بلند هست که ارزشِ جمع‌شدن داشته باشد؟ */
const isLong = (node) => {
  if (node == null || node === false) return false;
  if (typeof node === "string" || typeof node === "number") {
    return String(node).length > 80;
  }
  // هر چیزی که المانِ ری‌اکت یا فهرست است، عملاً چندخطی می‌شود
  return true;
};

/**
 * پیامِ بلندِ بدونِ توضیح را به «سرخط + بقیه» می‌شکند.
 *
 * خیلی از کادرها کلِ متن را در `message` گذاشته‌اند و توضیحی ندارند؛ بدونِ این،
 * همان‌ها سه‌خطی باقی می‌مانند. برشِ روی اولین مرزِ جمله است تا سرخط، جملهٔ
 * کاملِ خودش باشد نه نصفه.
 */
const splitLong = (msg) => {
  if (typeof msg !== "string" || msg.length <= 90) return null;
  let cut = -1;
  for (const mark of ["؛ ", ". ", " — ", "، "]) {
    const i = msg.indexOf(mark, 40);
    if (i > 0 && (cut < 0 || i < cut)) cut = i + mark.length;
  }
  if (cut < 0 || cut > 130) cut = msg.lastIndexOf(" ", 90) + 1 || 90;
  return [msg.slice(0, cut).trim(), msg.slice(cut).trim()];
};

export default function GxAlert({ description, message, ...rest }) {
  const [open, setOpen] = useState(false);
  const split = useMemo(
    () => (description ? null : splitLong(message)), [description, message]);
  if (split) {
    [message, description] = [split[0], split[1]];
  }
  const collapsible = useMemo(() => isLong(description), [description]);

  if (!collapsible) {
    return (
      <AntAlert
        message={message}
        description={description}
        {...rest}
        style={{ padding: "6px 12px", ...(rest.style || {}) }}
      />
    );
  }

  return (
    <AntAlert
      {...rest}
      style={{ padding: "6px 12px", ...(rest.style || {}) }}
      message={
        <span style={{ fontSize: 13, lineHeight: 1.8 }}>
          {message}
          <a
            onClick={(e) => { e.preventDefault(); setOpen((v) => !v); }}
            style={{ marginInlineStart: 8, fontSize: 12, whiteSpace: "nowrap" }}
          >
            {open ? <>کمتر <UpOutlined style={{ fontSize: 10 }} /></>
              : <>بیشتر <DownOutlined style={{ fontSize: 10 }} /></>}
          </a>
        </span>
      }
      description={open ? (
        // سقفِ ارتفاع تا فهرستِ بلند (مثلاً ۵۰ نفرِ جامانده) صفحه را نبلعد
        <div style={{ maxHeight: 240, overflowY: "auto", fontSize: 12 }}>
          {description}
        </div>
      ) : null}
    />
  );
}
